(function (global) {
  'use strict';

  const MAX_CAMPAIGN_SKUS = 6;
  const SKU_PATTERN = /^[A-Z0-9._/-]+$/;

  function normalizeSku(value) {
    return String(value || '').trim().toUpperCase();
  }

  function normalizeSkuList(rawValue, limit) {
    const maxItems = Math.min(Math.max(Number(limit) || MAX_CAMPAIGN_SKUS, 1), MAX_CAMPAIGN_SKUS);
    const seen = new Set();
    const normalized = [];

    String(rawValue || '')
      .split(';')
      .some((value) => {
        const sku = normalizeSku(value);

        if (!sku || sku.length > 80 || !SKU_PATTERN.test(sku) || seen.has(sku)) return false;

        seen.add(sku);
        normalized.push(sku);
        return normalized.length >= maxItems;
      });

    return normalized;
  }

  function skuMatchesBase(variantSku, baseSku) {
    const variant = normalizeSku(variantSku);
    const base = normalizeSku(baseSku);

    return Boolean(base && variant && (variant === base || variant.startsWith(base + '-')));
  }

  function buildPredictiveSearchUrl(routesRoot, sectionHandle, sku) {
    const root = String(routesRoot || '/').replace(/\/?$/, '/');
    const params = new URLSearchParams();

    params.set('q', normalizeSku(sku));
    params.set('resources[type]', 'product');
    params.set('resources[limit]', '10');
    params.set('resources[options][fields]', 'variants.sku');
    params.set('resources[options][unavailable_products]', 'hide');
    params.set('section_id', sectionHandle);

    return root + 'search/suggest?' + params.toString();
  }

  async function resolveCandidates(skus, fetchCandidate) {
    const settled = await Promise.allSettled(skus.map((sku) => fetchCandidate(sku)));
    const seenProductIds = new Set();

    return settled.reduce((candidates, result) => {
      if (result.status !== 'fulfilled' || !result.value) return candidates;

      const candidate = result.value;
      const productId = String(candidate.productId || '');
      if (!productId || seenProductIds.has(productId)) return candidates;

      seenProductIds.add(productId);
      candidates.push(candidate);
      return candidates;
    }, []);
  }

  function getCampaignSkus(search) {
    const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
    const rawSkus = params.get('meta_products') || params.get('meta-products');
    return normalizeSkuList(rawSkus);
  }

  async function requestCandidate(sku, root, options) {
    const settings = options || {};
    const fetchImplementation = settings.fetch || global.fetch;
    if (typeof fetchImplementation !== 'function') return null;

    const sectionHandle = root.dataset.sectionHandle || 'product-recommendations-utm-optimized';
    const routesRoot = settings.routesRoot || (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/';
    const timeoutMs = Number(settings.timeoutMs) || 5000;
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timeoutId = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;

    try {
      const response = await fetchImplementation(
        buildPredictiveSearchUrl(routesRoot, sectionHandle, sku),
        {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: controller ? controller.signal : undefined
        }
      );

      if (!response.ok) return null;

      const html = await response.text();
      const template = global.document.createElement('template');
      template.innerHTML = html.trim();
      const candidate = template.content.querySelector('[data-utm-product-candidate]');

      if (!candidate || candidate.dataset.utmBaseSku !== sku) return null;

      return {
        productId: candidate.dataset.productId,
        html: candidate.outerHTML
      };
    } catch (_error) {
      return null;
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  async function renderRecommendations(root, options) {
    if (!root) return [];

    const settings = options || {};
    const search = settings.search !== undefined
      ? settings.search
      : (global.location ? global.location.search : '');
    const skus = getCampaignSkus(search);
    const target = root.querySelector('[data-utm-products-row]');

    if (!target || !skus.length) {
      root.hidden = true;
      root.classList.remove('is-loading', 'is-ready');
      root.removeAttribute('aria-busy');
      return [];
    }

    root.hidden = false;
    root.classList.add('is-loading');
    root.classList.remove('is-ready');
    root.dataset.utmLoadingCount = String(skus.length);
    root.setAttribute('aria-busy', 'true');

    const fetchCandidate = settings.fetchCandidate || ((sku) => requestCandidate(sku, root, settings));
    const candidates = await resolveCandidates(skus, fetchCandidate);

    if (root.isConnected === false) return candidates;

    target.innerHTML = '';
    candidates.forEach((candidate) => {
      target.insertAdjacentHTML('beforeend', candidate.html);
    });

    root.classList.remove('is-loading');
    root.removeAttribute('aria-busy');
    root.hidden = candidates.length === 0;
    root.classList.toggle('is-ready', candidates.length > 0);

    if (candidates.length && global.document && typeof global.CustomEvent === 'function') {
      global.document.dispatchEvent(new global.CustomEvent('collection:rerendered'));
    }

    return candidates;
  }

  function initRecommendations(scope) {
    if (!global.document) return;

    const context = scope && scope.querySelectorAll ? scope : global.document;
    const roots = [];

    if (context.matches && context.matches('[data-utm-product-recommendations]')) roots.push(context);
    context.querySelectorAll('[data-utm-product-recommendations]').forEach((root) => roots.push(root));

    roots.forEach((root) => {
      if (root.dataset.utmRecommendationsBound === 'true') return;
      root.dataset.utmRecommendationsBound = 'true';
      renderRecommendations(root);
    });
  }

  const api = {
    MAX_CAMPAIGN_SKUS,
    normalizeSkuList,
    skuMatchesBase,
    buildPredictiveSearchUrl,
    resolveCandidates,
    renderRecommendations,
    initRecommendations
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  global.UTMProductRecommendations = api;

  if (global.document) {
    initRecommendations(global.document);

    if (!global.__utmProductRecommendationsEventsBound) {
      global.__utmProductRecommendationsEventsBound = true;

      if (global.document.readyState === 'loading') {
        global.document.addEventListener('DOMContentLoaded', () => initRecommendations(global.document));
      }

      global.document.addEventListener('shopify:section:load', (event) => initRecommendations(event.target));
    }
  }
})(typeof globalThis !== 'undefined' ? globalThis : this);
